import type { WorkflowInput, WorkflowNodeConnector } from "@/stores/workflowsStore";
import type { Edge, Node } from "@xyflow/react";

import {
  calculateApiNodeId,
  computeDisplayLabel,
  CONNECTOR_VARIABLE_FIELDS,
  DEFAULT_CATEGORIES,
  findAllForEachAncestors,
  findNonParentConnectorNodes,
  WORKFLOW_ID_VAR,
  findParentNodes,
  generateX402FallbackCategoriesHierarchical,
  type HierarchicalVariable,
  type NodeDefinition,
  type NodeVariables,
  type SchemaProperty,
  type VariableCategory,
} from "./extract-workflow-variables";
import { getEffectiveResultSchema } from "./extract-workflow-variables-flat";

// ============================================================================
// HIERARCHICAL VARIABLE EXTRACTION (for new tree-based variable picker)
// ============================================================================

/**
 * Extract nested variables from schema properties into a hierarchical tree structure.
 * This preserves parent-child relationships for the tree view.
 */
function extractHierarchicalVariables(
  basePath: string,
  properties: Record<string, SchemaProperty>,
  maxDepth: number = 5,
  currentDepth: number = 0,
): HierarchicalVariable[] {
  if (currentDepth >= maxDepth) return [];

  const variables: HierarchicalVariable[] = [];

  Object.entries(properties).forEach(([fieldName, fieldSchema]) => {
    const fieldPath = `${basePath}.${fieldName}`;
    const variable: HierarchicalVariable = {
      id: `{{${fieldPath}}}`,
      key: fieldName,
      label: fieldSchema.description || fieldName,
    };

    // If this field has nested properties, recurse to get children
    // Note: JSON Schema allows omitting "type" when "properties" is defined
    if (fieldSchema.properties) {
      variable.children = extractHierarchicalVariables(fieldPath, fieldSchema.properties, maxDepth, currentDepth + 1);
    }

    variables.push(variable);
  });

  return variables;
}

/**
 * Determine the category key for a field based on schema or path.
 * Uses x-category from schema if available, otherwise infers from field name.
 */
function getCategoryKey(fieldName: string, fieldSchema: SchemaProperty): string {
  // Check for explicit category annotation
  const explicitCategory = (fieldSchema as Record<string, unknown>)["x-category"];
  if (typeof explicitCategory === "string") {
    return explicitCategory;
  }

  // Infer category from field name
  if (fieldName === "paymentDetails" || fieldName.toLowerCase().includes("payment")) {
    return "payment";
  }
  if (fieldName === "requestDetails" || fieldName.toLowerCase().includes("request")) {
    return "request";
  }

  return "output";
}

/**
 * Get category metadata (label and order) for a category key.
 */
function getCategoryMeta(categoryKey: string, schema?: Record<string, unknown>): { label: string; order: number } {
  // Check for custom labels in schema
  const customLabels = schema?.["x-categoryLabels"] as Record<string, string> | undefined;
  const customOrder = schema?.["x-categoryOrder"] as string[] | undefined;

  let label = DEFAULT_CATEGORIES[categoryKey]?.label || categoryKey;
  let order = DEFAULT_CATEGORIES[categoryKey]?.order || 99;

  if (customLabels?.[categoryKey]) {
    label = customLabels[categoryKey];
  }

  if (customOrder) {
    const idx = customOrder.indexOf(categoryKey);
    if (idx !== -1) {
      order = idx + 1;
    }
  }

  return { label, order };
}

/**
 * Extract hierarchical variables for a single node.
 * Organizes variables by categories based on schema annotations or field inference.
 */
function getNodeHierarchicalVariables(
  nodeId: string,
  nodeType: string,
  nodeName: string,
  nodeDefinition?: NodeDefinition,
  node?: Node,
): NodeVariables {
  const categoriesMap = new Map<string, VariableCategory>();

  // Get effective result schema (node-specific or from definition)
  const effectiveResultSchema = getEffectiveResultSchema(node, nodeDefinition);

  if (effectiveResultSchema?.properties) {
    const resultProperties = effectiveResultSchema.properties;

    // Process each top-level field and assign to categories
    Object.entries(resultProperties).forEach(([fieldName, fieldSchema]) => {
      const categoryKey = getCategoryKey(fieldName, fieldSchema);
      const categoryMeta = getCategoryMeta(categoryKey, effectiveResultSchema as Record<string, unknown>);

      // Get or create category
      if (!categoriesMap.has(categoryKey)) {
        categoriesMap.set(categoryKey, {
          key: categoryKey,
          label: categoryMeta.label,
          order: categoryMeta.order,
          variables: [],
        });
      }
      const category = categoriesMap.get(categoryKey)!;

      // Create variable for this field
      const variable: HierarchicalVariable = {
        id: `{{${nodeId}.result.${fieldName}}}`,
        key: fieldName,
        label: fieldSchema.description || fieldName,
      };

      // Extract nested children if this field has properties
      // Note: JSON Schema allows omitting "type" when "properties" is defined
      if (fieldSchema.properties) {
        variable.children = extractHierarchicalVariables(`${nodeId}.result.${fieldName}`, fieldSchema.properties);
      }

      // For arrays with object items, extract item properties as children
      if (fieldSchema.type === "array" && fieldSchema.items?.properties) {
        variable.children = extractHierarchicalVariables(
          `${nodeId}.result.${fieldName}[0]`,
          fieldSchema.items.properties,
        );
      }

      category.variables.push(variable);
    });

    // Add the full result object to Output category
    // (except manual triggers — their result only contains triggeredAt)
    if (nodeType !== "manual") {
      const outputCategory = categoriesMap.get("output");
      if (outputCategory) {
        outputCategory.variables.push({
          id: `{{${nodeId}.result}}`,
          key: "result",
          label: "Full result object",
        });
      }
    }
  } else if (nodeType === "call-x402-endpoint") {
    // Fallback for x402 nodes without schema - uses shared constants
    const fallbackCategories = generateX402FallbackCategoriesHierarchical(nodeId);
    fallbackCategories.forEach(cat => categoriesMap.set(cat.key, cat));
  } else {
    // Generic fallback for other node types
    const outputCategory: VariableCategory = {
      key: "output",
      label: "Output",
      order: 1,
      variables: [
        { id: `{{${nodeId}.result.value}}`, key: "value", label: "Result value" },
        { id: `{{${nodeId}.result.data}}`, key: "data", label: "Result data" },
        { id: `{{${nodeId}.result}}`, key: "result", label: "Full result object" },
      ],
    };
    categoriesMap.set("output", outputCategory);
  }

  // Add payload/config variables if present
  const nodePayload = node?.data.payload as Record<string, unknown> | undefined;
  if (nodePayload && Object.keys(nodePayload).length > 0) {
    const configCategory: VariableCategory = {
      key: "config",
      label: "Config",
      order: 10,
      variables: [],
    };

    Object.entries(nodePayload).forEach(([fieldName, fieldValue]) => {
      if (fieldValue === null || fieldValue === undefined) return;
      // Skip internal fields (starting with _)
      if (fieldName.startsWith("_")) return;
      // Skip schema metadata fields and block-internal fields
      if (
        ["inputSchema", "outputSchema", "inputType", "outputType", "blockId", "blockVersion", "inputMapping"].includes(
          fieldName,
        )
      )
        return;

      configCategory.variables.push({
        id: `{{${nodeId}.payload.${fieldName}}}`,
        key: fieldName,
        label: `Config: ${fieldName}`,
      });
    });

    if (configCategory.variables.length > 0) {
      categoriesMap.set("config", configCategory);
    }
  }

  // Add connector variables if the node has a connector with a known type
  const connectorType = (node?.data.connector as WorkflowNodeConnector | undefined)?.type;
  if (connectorType) {
    const fields = CONNECTOR_VARIABLE_FIELDS[connectorType];
    if (fields && fields.length > 0) {
      const connectorCategory: VariableCategory = {
        key: "connector",
        label: DEFAULT_CATEGORIES.connector.label,
        order: DEFAULT_CATEGORIES.connector.order,
        variables: fields.map(field => ({
          id: `{{${nodeId}.connector.${field.name}}}`,
          key: field.name,
          label: field.description,
        })),
      };
      categoriesMap.set("connector", connectorCategory);
    }
  }

  // Sort categories by order
  const sortedCategories = Array.from(categoriesMap.values()).sort((a, b) => a.order - b.order);

  return {
    nodeId,
    nodeName,
    nodeType,
    categories: sortedCategories,
  };
}

/**
 * Extract all workflow variables in a hierarchical structure.
 * Returns an array of NodeVariables, each containing categories of variables.
 *
 * This is used by the new tree-based variable picker modal.
 *
 * @param cache - Optional cache to avoid recomputing variables for the same node
 */
export function extractHierarchicalWorkflowVariables(
  currentNodeId: string,
  allNodes: Node[],
  allEdges: Edge[],
  nodeDefinitions?: NodeDefinition[],
  cache?: Map<string, NodeVariables>,
  inputSchema?: WorkflowInput[],
  stepNumbers?: Map<string, number>,
): NodeVariables[] {
  const nodeVariables: NodeVariables[] = [];

  // Find all parent nodes (nodes that can provide variables to the current node)
  const parentNodeIds = findParentNodes(currentNodeId, allEdges);

  // For each parent node, extract hierarchical variables
  parentNodeIds.forEach(parentNodeId => {
    // Check cache first to avoid redundant computations
    if (cache?.has(parentNodeId)) {
      nodeVariables.push(cache.get(parentNodeId)!);
      return;
    }

    const node = allNodes.find(n => n.id === parentNodeId);
    if (!node || node.type === "placeholder") return;

    // Get node API ID using the same calculation as extractWorkflowVariables
    // This ensures variable IDs are consistent between flat and hierarchical pickers
    const apiNodeId = calculateApiNodeId(node, allNodes, allEdges);
    const nodeType = (node.data.type as string) || "unknown";
    const nodeName = (node.data.name as string) || nodeType;

    // Find matching definition.
    // For block nodes, look up by "block:${blockId}" to find the synthetic definition
    // created by useBlockDefinitions (each block has a unique outputSchema).
    const definitionLookupType =
      nodeType === "block" ? `block:${(node.data.payload as Record<string, unknown>)?.blockId || ""}` : nodeType;
    const definition = nodeDefinitions?.find(def => def.type === definitionLookupType);

    // Extract hierarchical variables for this node
    const variables = getNodeHierarchicalVariables(apiNodeId, nodeType, nodeName, definition, node);

    // Set step number and display labels if stepNumbers provided
    const stepNum = stepNumbers?.get(parentNodeId);
    if (stepNum != null) {
      variables.stepNumber = stepNum;
    }
    if (stepNumbers) {
      for (const cat of variables.categories) {
        const setDisplayLabels = (vars: HierarchicalVariable[]) => {
          for (const v of vars) {
            v.displayLabel = computeDisplayLabel(v.id, nodeName, stepNum);
            if (v.children) setDisplayLabels(v.children);
          }
        };
        setDisplayLabels(cat.variables);
      }
    }

    // For the root trigger node, inject inputSchema fields as a separate "Inputs" category.
    // These use the "input" section (e.g., {{root.input.userId}}) to differentiate from
    // trigger result fields (e.g., {{root.result.triggeredAt}}).
    if (apiNodeId === "root" && inputSchema && inputSchema.length > 0) {
      const inputVars: HierarchicalVariable[] = inputSchema
        .filter(input => input.key)
        .map(input => {
          const varId = `{{root.input.${input.key}}}`;
          return {
            id: varId,
            key: input.key,
            label: input.description || `Input: ${input.key}`,
            displayLabel: stepNumbers ? computeDisplayLabel(varId, nodeName, stepNum) : undefined,
          };
        });

      if (inputVars.length > 0) {
        variables.categories.unshift({
          key: "input",
          label: "Inputs",
          order: 0,
          variables: inputVars,
        });
      }
    }

    // Only add if there are variables
    if (variables.categories.some(cat => cat.variables.length > 0)) {
      // Store in cache for reuse
      if (cache) {
        cache.set(parentNodeId, variables);
      }
      nodeVariables.push(variables);
    }
  });

  // Extract connector variables from non-parent nodes (e.g., downstream connector
  // nodes whose wallet address should be referenceable from upstream nodes).
  for (const cn of findNonParentConnectorNodes(currentNodeId, parentNodeIds, allNodes, allEdges, stepNumbers)) {
    nodeVariables.push({
      nodeId: cn.apiNodeId,
      nodeName: cn.nodeName,
      nodeType: cn.nodeType,
      stepNumber: cn.stepNum,
      categories: [
        {
          key: "connector",
          label: DEFAULT_CATEGORIES.connector.label,
          order: DEFAULT_CATEGORIES.connector.order,
          variables: cn.fields.map(field => {
            const varId = `{{${cn.apiNodeId}.connector.${field.name}}}`;
            return {
              id: varId,
              key: field.name,
              label: field.description,
              displayLabel: stepNumbers ? computeDisplayLabel(varId, cn.nodeName, cn.stepNum) : undefined,
            };
          }),
        },
      ],
    });
  }

  // Check for for-each ancestors and add loop variables
  const forEachAncestors = findAllForEachAncestors(currentNodeId, allNodes, allEdges);
  if (forEachAncestors.length > 0) {
    const loopVariables: HierarchicalVariable[] = [
      { id: "{{$item}}", key: "$item", label: "Current loop item", displayLabel: "Current Item" },
      { id: "{{$index}}", key: "$index", label: "Current loop index (0-based)", displayLabel: "Current Index" },
    ];

    // Depth >= 2 → $parent_item, $parent_index
    if (forEachAncestors.length >= 2) {
      const parentName = (forEachAncestors[1].data.name as string) || "for-each";
      loopVariables.push(
        {
          id: "{{$parent_item}}",
          key: "$parent_item",
          label: `Parent loop item (${parentName})`,
          displayLabel: "Parent Item",
        },
        {
          id: "{{$parent_index}}",
          key: "$parent_index",
          label: `Parent loop index (${parentName})`,
          displayLabel: "Parent Index",
        },
      );
    }

    // Outer ancestors → $for.{apiNodeId}.item/index (skip innermost, already covered by $item/$index)
    for (let i = 1; i < forEachAncestors.length; i++) {
      const ancestor = forEachAncestors[i];
      const apiNodeId = calculateApiNodeId(ancestor, allNodes, allEdges);
      const name = (ancestor.data.name as string) || "for-each";
      loopVariables.push(
        {
          id: `{{$for.${apiNodeId}.item}}`,
          key: `$for.${apiNodeId}.item`,
          label: `Loop item (${name})`,
          displayLabel: `For ${apiNodeId}: item`,
        },
        {
          id: `{{$for.${apiNodeId}.index}}`,
          key: `$for.${apiNodeId}.index`,
          label: `Loop index (${name})`,
          displayLabel: `For ${apiNodeId}: index`,
        },
      );
    }

    const loopVarsNode: NodeVariables = {
      nodeId: "_loop",
      nodeName: "Loop Variables",
      nodeType: "loop",
      categories: [
        {
          key: "loop",
          label: "Loop",
          order: 0,
          variables: loopVariables,
        },
      ],
    };
    nodeVariables.unshift(loopVarsNode);
  }

  // Add system variables (available in all contexts)
  const systemVarsNode: NodeVariables = {
    nodeId: "$system",
    nodeName: "System",
    nodeType: "system",
    categories: [
      {
        key: "system",
        label: "System",
        order: 99,
        variables: [
          {
            id: WORKFLOW_ID_VAR,
            key: "$workflowId",
            label: "Current workflow ID",
            displayLabel: "Workflow ID",
          },
        ],
      },
    ],
  };
  nodeVariables.push(systemVarsNode);

  return nodeVariables;
}
